import type { Archive, GameTask, PendingHuntContext } from "../../domain/types";
import { acquireItem } from "../../domain/itemRegistry";
import { applyTaskReward } from "../../domain/taskRewards";
import {
  buildFallbackTable,
  materialDefinition,
  rollPool,
  seededRandom,
  type DropEntry,
  type DropMethod,
} from "../../domain/dropTables";
export interface SettlementRoll {
  method: DropMethod;
  entry: DropEntry;
  revealed: boolean;
}
export interface PreparedSettlement {
  task: GameTask;
  rolls: SettlementRoll[];
}
export function pendingCompletedTask(a: Archive) {
  return (
    a.tasks.available.find(
      (t) =>
        t.id === a.tasks.activeTaskId &&
        t.status === "completed" &&
        !t.settlementDone,
    ) ?? null
  );
}
export function prepareSettlement(
  a: Archive,
  task: GameTask,
): PreparedSettlement {
  const monsterId = task.targetMonsterIds[0] ?? "guild-supplies";
  const name = task.targetMonsterNames[0] ?? "任务目标";
  if (!["hunt", "capture"].includes(task.questType)) return { task, rolls: [] };
  const table = buildFallbackTable({ id: monsterId, name, star: task.star });
  const state = a.monsters[monsterId];
  const methods: DropMethod[] =
    task.questType === "capture"
      ? ["capture", "target_reward"]
      : [
          "carve_body",
          ...(state?.tailSevered ? ["carve_tail" as const] : []),
          "target_reward",
        ];
  const rolls = methods.flatMap((method, i) => {
    const pool = table.pools.find((p) => p.method === method)!;
    return rollPool(pool, seededRandom(`${a.name}:${task.id}:${i}`)).map(
      (entry) => ({ method, entry, revealed: false }),
    );
  });
  return { task, rolls };
}
export function confirmSettlement(a: Archive, prepared: PreparedSettlement) {
  const currentTask = a.tasks.available.find((task) => task.id === prepared.task.id);
  if (!currentTask || currentTask.settlementDone) return a;
  let next = structuredClone(a);
  for (const r of prepared.rolls)
    next.itemRegistry = acquireItem(
      next.itemRegistry,
      materialDefinition(
        r.entry,
        prepared.task.targetMonsterIds[0] ?? "unknown",
      ),
      r.entry.quantity,
      `任务 ${prepared.task.id}`,
    );
  const before = next.player.hunter.hr;
  const reward = applyTaskReward(next, prepared.task);
  next = reward.archive;
  const task = next.tasks.available.find((t) => t.id === prepared.task.id)!;
  task.settlementDone = true;
  next.tasks.available = next.tasks.available.filter((t) => t.id !== task.id);
  next.tasks.history.push(task);
  next.tasks.activeTaskId = null;
  const grouped = new Map<
    string,
    { key: string; name: string; quantity: number }
  >();
  for (const r of prepared.rolls) {
    const g = grouped.get(r.entry.materialKey) ?? {
      key: r.entry.materialKey,
      name: r.entry.name,
      quantity: 0,
    };
    g.quantity += r.entry.quantity;
    grouped.set(r.entry.materialKey, g);
  }
  const context: PendingHuntContext = {
    taskTitle: task.title,
    materials: [...grouped.values()],
    reward: task.reward,
    hrBefore: before,
    hrAfter: next.player.hunter.hr,
  };
  next.pendingHuntContext = context;
  return next;
}
